/**
 * Builders for the visible "Contains AI-generated content" overlay. Pure string
 * builders (no DOM) so they run identically in the browser (static-ad export,
 * motion-ad preview) and in Node (video-export worker). For static ads the
 * caller splices the returned fragment into the ad; for motion ads
 * `buildDisclaimerCompositionLayer` returns a whole scene layer.
 */

import {
  DISCLAIMER_FONT_FAMILY,
  DISCLAIMER_FONT_SIZE,
  DISCLAIMER_TEXT,
} from "./constants";
import type { DisclaimerPosition } from "./types";

const MARGIN = 16;
const PADDING_X = 10;
const PADDING_Y = 6;
const CORNER_RADIUS = 6;
const BACKGROUND = "rgba(0, 0, 0, 0.65)";
const FOREGROUND = "#ffffff";

/**
 * Optional overlay font overrides. Either field may be omitted, falling back to
 * the shared `DISCLAIMER_FONT_*` defaults. Callers pass the brand's configured
 * font here; the geometry (pill size, text baseline) scales with `size`.
 */
export type DisclaimerOverlayFont = { family?: string; size?: number };

function resolveFont(font?: DisclaimerOverlayFont): {
  family: string;
  size: number;
} {
  return {
    family: font?.family ?? DISCLAIMER_FONT_FAMILY,
    size: font?.size ?? DISCLAIMER_FONT_SIZE,
  };
}

/** Marks the injected node so it can be identified / deduped downstream. */
export const DISCLAIMER_MARKER_ATTR = "data-ai-disclaimer";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeXml(value: string): string {
  return escapeHtml(value).replace(/'/g, "&apos;");
}

const LINE_HEIGHT = 1.2;

/** The pill's look, shared by every HTML form of the overlay. */
function pillStyles(font: { family: string; size: number }): string[] {
  return [
    `background:${BACKGROUND}`,
    `color:${FOREGROUND}`,
    `padding:${PADDING_Y}px ${PADDING_X}px`,
    `border-radius:${CORNER_RADIUS}px`,
    `font-family:${font.family}`,
    `font-size:${font.size}px`,
    `line-height:${LINE_HEIGHT}`,
    "font-weight:500",
  ];
}

export function buildDisclaimerHtmlOverlay(args: {
  position: DisclaimerPosition;
  text?: string;
  font?: DisclaimerOverlayFont;
}): string {
  const text = args.text ?? DISCLAIMER_TEXT;
  const font = resolveFont(args.font);
  const vertical = args.position.startsWith("top") ? "top" : "bottom";
  const horizontal = args.position.endsWith("left") ? "left" : "right";
  const style = [
    "position:absolute",
    `${vertical}:${MARGIN}px`,
    `${horizontal}:${MARGIN}px`,
    ...pillStyles(font),
    "box-sizing:border-box",
    `max-width:min(80%, calc(100% - ${MARGIN * 2}px))`,
    "white-space:normal",
    "overflow-wrap:anywhere",
    "pointer-events:none",
  ].join(";");
  return `<div ${DISCLAIMER_MARKER_ATTR}="true" style="${style}">${escapeHtml(text)}</div>`;
}

/** Layer id of the pill inside the scene built by `buildDisclaimerHtmlScene`. */
export const DISCLAIMER_LAYER_ID = "ai-disclaimer";

/**
 * A full-frame HTML scene holding only the pill, in the shape the ad editor's
 * `parseDoc` reads: a sized root and one absolutely placed auto-width text
 * layer. The editor model has no `right`/`bottom` offsets, so the pill hugs
 * its text (`width:max-content`) and right positions anchor its right edge
 * with `translateX(-100%)`; its height is one line plus padding, so the
 * bottom positions place the top edge from that.
 */
export function buildDisclaimerHtmlScene(args: {
  width: number;
  height: number;
  position: DisclaimerPosition;
  text?: string;
  font?: DisclaimerOverlayFont;
}): string {
  const text = args.text ?? DISCLAIMER_TEXT;
  const font = resolveFont(args.font);
  const pillHeight = font.size * LINE_HEIGHT + PADDING_Y * 2;
  const left = args.position.endsWith("left") ? MARGIN : args.width - MARGIN;
  const top = args.position.startsWith("top")
    ? MARGIN
    : args.height - MARGIN - pillHeight;
  const style = [
    "position:absolute",
    `left:${left}px`,
    `top:${top}px`,
    "width:max-content",
    "white-space:nowrap",
    ...(args.position.endsWith("left") ? [] : ["transform:translateX(-100%)"]),
    ...pillStyles(font),
  ].join(";");
  const rootStyle =
    `position:relative;width:${args.width}px;height:${args.height}px;` +
    "overflow:hidden;background:transparent;";
  return (
    `<div style="${rootStyle}">` +
    `<div data-ht-id="${DISCLAIMER_LAYER_ID}" style="${style}">${escapeHtml(text)}</div>` +
    "</div>"
  );
}

/**
 * Build the full-frame, full-duration composition layer that renders the
 * visible disclosure over a motion ad. Shared by the video-export worker (the
 * real Remotion render) and the editor preview so both produce an identical
 * overlay. Returns a plain `CompositionLayer`-shaped object to avoid coupling
 * this package to the renderer's types.
 */
export function buildDisclaimerCompositionLayer(args: {
  width: number;
  height: number;
  durationInFrames: number;
  position: DisclaimerPosition;
  text?: string;
  font?: DisclaimerOverlayFont;
  id?: string;
}): {
  id: string;
  component: string;
  from: number;
  duration: number;
  props: Record<string, unknown>;
} {
  return {
    id: args.id ?? DISCLAIMER_LAYER_ID,
    component: "@ad-studio/html-overlay",
    from: 0,
    duration: args.durationInFrames,
    props: {
      src: buildDisclaimerHtmlScene({
        width: args.width,
        height: args.height,
        position: args.position,
        text: args.text,
        font: args.font,
      }),
    },
  };
}

export function buildDisclaimerSvgOverlay(args: {
  width: number;
  height: number;
  position: DisclaimerPosition;
  text?: string;
  font?: DisclaimerOverlayFont;
}): string {
  const text = args.text ?? DISCLAIMER_TEXT;
  const font = resolveFont(args.font);
  // SVG has no text layout, so we approximate the pill width from the glyph
  // count. ~0.6em per character is a safe overestimate for the system font.
  const approxCharWidth = font.size * 0.6;
  const pillWidth = Math.round(text.length * approxCharWidth + PADDING_X * 2);
  const pillHeight = font.size + PADDING_Y * 2;
  const x = args.position.endsWith("left")
    ? MARGIN
    : Math.max(MARGIN, args.width - MARGIN - pillWidth);
  const y = args.position.startsWith("top")
    ? MARGIN
    : Math.max(MARGIN, args.height - MARGIN - pillHeight);
  const textX = x + PADDING_X;
  // Vertically center the text within the pill (0.35em below the middle puts
  // the alphabetic baseline roughly on center for this font size).
  const textY = y + pillHeight / 2 + font.size * 0.35;
  return [
    `<g ${DISCLAIMER_MARKER_ATTR}="true">`,
    `<rect x="${x}" y="${y}" width="${pillWidth}" height="${pillHeight}" rx="${CORNER_RADIUS}" ry="${CORNER_RADIUS}" fill="${BACKGROUND}"/>`,
    `<text x="${textX}" y="${textY}" font-family="${escapeXml(font.family)}" font-size="${font.size}" font-weight="500" fill="${FOREGROUND}">${escapeXml(text)}</text>`,
    `</g>`,
  ].join("");
}
